import {
  externalLinkReportIssue,
  ExternalReportIssueModel,
} from '@/apis/shipment';
import { SingleShipmentDataExtended } from '@/apis/types';
import { CustomButton, CustomTitle } from '@/components';
import useUnauthorize from '@/hooks/useUnauthorize';
import { DownOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, message, Row, Spin, TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useState } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ReportTimeFormValues {
  requested_delivery_date?: Dayjs;
  requested_window_start_hour?: Dayjs;
  requested_window_end_hour?: Dayjs;
}

interface TrackingLinkReportTimeIssueProps {
  deliveryData?: string;
  token?: string;
  shipment?: Partial<SingleShipmentDataExtended>;
  fetchSingleShipmentData?: (showSpinner: boolean) => void;
  refetchSharePageData?: () => void;
  isSharePage?: boolean;

  handleCardActions?: (
    showCard: boolean,
    isOpen: boolean,
    name: string
  ) => void;
}

const TrackingLinkReportTimeIssue: React.FC<
  TrackingLinkReportTimeIssueProps
> = ({
  deliveryData,
  handleCardActions,
  token,
  shipment,
  refetchSharePageData,
}) => {
  const { unauthorize } = useUnauthorize();
  const [form] = Form.useForm<ReportTimeFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const formatted = dayjs(deliveryData).format('ddd, MMM D, h A');
  const shipmentTimezone = shipment?.timezone || 'America/Los_Angeles';

  const onFinish = (values: ReportTimeFormValues): void => {
    setSubmitting(true);

    const model: ExternalReportIssueModel = {
      token,
      issue_type: 'DELIVERY_TIME_PREFERENCE',
      submitter_channel: null,
      submitter_identifier: null,
      timezone: shipmentTimezone,

      requested_delivery_date:
        values.requested_delivery_date?.format('YYYY-MM-DD'),

      requested_window_start_hour: values.requested_window_start_hour?.hour(),

      requested_window_end_hour: values.requested_window_end_hour?.hour(),
    };

    externalLinkReportIssue(model)
      .then((res) => {
        if (res?.data?.data?.is_success) {
          message.success(res?.data?.data?.message);

          // Refresh appropriate data based on page type
          refetchSharePageData?.();

          handleCardActions?.(false, false, '');
          form.resetFields();
        } else {
          message.error(res?.data?.data?.message || 'Something went wrong');
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  React.useEffect(() => {
    form.setFieldsValue({
      requested_delivery_date: dayjs().tz(shipmentTimezone).startOf('day'),
    });
  }, [form, shipmentTimezone]);
  return (
    <div>
      <CustomTitle
        text={'Current delivery date'}
        fontSize={16}
        fontWeight={600}
        lineHeight='20px'
        color='var(--text-primary)'
        marginBottom='0'
      />
      <CustomTitle
        text={formatted || '--'}
        fontSize={14}
        fontWeight={400}
        lineHeight='20px'
        color='var(--text-primary)'
        marginTop='12px'
        marginBottom='0'
      />

      <CustomTitle
        text={'New delivery preference'}
        fontSize={16}
        fontWeight={600}
        lineHeight='20px'
        color='var(--text-primary)'
        marginTop='24px'
        marginBottom='24px'
      />

      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <Form.Item
              className='w-full'
              label='Date'
              name='requested_delivery_date'
              rules={[
                {
                  required: true,
                  message: 'Please select a delivery date',
                },
                {
                  validator(_, value) {
                    if (!value) return Promise.resolve();

                    const selected = value.clone().startOf('day');
                    const today = dayjs().tz(shipmentTimezone).startOf('day');

                    if (selected.isBefore(today)) {
                      return Promise.reject(
                        new Error('Date must not be in the past')
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                className='external-custom-timepicker timepicker-style'
                disabledDate={(current) =>
                  current &&
                  current.isBefore(
                    dayjs().tz(shipmentTimezone).startOf('day'),
                    'day'
                  )
                }
                format='ddd, MMM D'
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              className='custom-input form-input-style w-full'
              label='Between'
              required
            >
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Form.Item
                    name='requested_window_start_hour'
                    dependencies={['requested_delivery_date']}
                    rules={[
                      {
                        required: true,
                        message: 'Please select a start time',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const selectedDate = getFieldValue(
                            'requested_delivery_date'
                          ) as Dayjs;

                          if (!selectedDate || !value) {
                            return Promise.resolve();
                          }

                          const shipmentNow = dayjs().tz(shipmentTimezone);

                          const isToday = selectedDate.isSame(
                            shipmentNow,
                            'day'
                          );

                          if (isToday && value.hour() <= shipmentNow.hour()) {
                            return Promise.reject(
                              new Error('Selected time must be in the future')
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      format='HH'
                      showNow={false}
                      placeholder='Select time'
                      classNames={{
                        popup: {
                          root: 'external-timepicker-dropdown',
                        },
                      }}
                      suffixIcon={
                        <DownOutlined
                          style={{
                            color: 'var(--text-secondary)',
                            fontSize: '12px',
                          }}
                        />
                      }
                      className='custom-timepicker timepicker-style'
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    name='requested_window_end_hour'
                    dependencies={['requested_window_start_hour']}
                    rules={[
                      {
                        required: true,
                        message: 'Please select an end time',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const start = getFieldValue(
                            'requested_window_start_hour'
                          );

                          if (!start || !value || value.isAfter(start)) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error('End time must be after start time')
                          );
                        },
                      }),
                    ]}
                  >
                    <TimePicker
                      format='HH'
                      showNow={false}
                      placeholder='Select time'
                      classNames={{
                        popup: {
                          root: 'external-timepicker-dropdown',
                        },
                      }}
                      suffixIcon={
                        <DownOutlined
                          style={{
                            color: 'var(--text-secondary)',
                            fontSize: '12px',
                          }}
                        />
                      }
                      className='custom-timepicker timepicker-style'
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>

        <div className='external-issue-time-bottom-btns'>
          <Row gutter={[0, 8]} style={{ marginTop: '0' }}>
            <CustomButton
              loader={submitting ? <Spin size='small' /> : null}
              text='Submit'
              width='100%'
              borderRadius='8'
              bgColor={submitting ? '#F3F3F3' : '#000'}
              border='0'
              fontSize='16'
              fontWeight={600}
              htmlType='submit'
              disabled={submitting}
              className='btn-responsive'
            />
            <CustomButton
              width='100%'
              text='Cancel'
              bgColor='transparent'
              color='black'
              fontSize='16px'
              fontWeight='600'
              boxShadow='none'
              className='btn-responsive'
              onClick={() => handleCardActions?.(true, false, '')}
            />
          </Row>
        </div>
      </Form>
    </div>
  );
};

export default TrackingLinkReportTimeIssue;
